import { useCallback, useEffect } from "react";
import { axiosInstance, setAuthHeaders } from "api";
import { useRecoilState, useResetRecoilState } from "recoil";
import { userAtom } from "recoil/userAtom";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { patientAtom } from "recoil/patientAtom";
import { Sheet, Stack } from "@mui/joy";
import { getRedirectPath } from "logic/logics";
import "dayjs/locale/ko";
import dayjs from "dayjs";
import EmptyPage from "static/image/common/EmptyPage";
import { USER_DETAIL_ENDPOINT, USER_DOMAIN, userApi } from "api/userApi";
import { useQuery } from "@tanstack/react-query";
import duration from "dayjs/plugin/duration";
import useOpenTask from "hooks/useOpenTask";
import { sendMessage } from "utils/webview";
import useRemoveTab from "hooks/useRemoveTab";
import useRefetchTaskList from "hooks/useRefetchTaskList";
import useIsInappWebview from "hooks/useIsInappWebview";
import Task from "components/task/Task";
import { isMobile, isTablet } from "react-device-detect";
import { deepLinkData } from "data/deepLinkData";
import useSetConceptData from "hooks/useSetConceptData";
import useDetectIOS from "hooks/useDetectIOS";
import Assistant from "components/coachRightSidebar/assistant/Assistant";
import SidebarLayoutForCoach from "components/sidebar/SidebarLayoutForCoach";
import { CHAT_DETAIL_ENDPOINT, CHAT_DOMAIN, chatApi } from "api/chatApi";
import { userWebChatAtom } from "recoil/userWebChatAtom";
import SidebarLayoutPatient from "components/sidebar/SideBarLayoutForPatient";
import useUnifiedNavigate from "../../hooks/useUnifiedNavigate";

export default function Home() {
  const navigate = useNavigate();
  const { taskKey } = useParams();

  const [searchParams] = useSearchParams();
  const isPayment = searchParams.get("tab") === "payment";
  const pid =
    searchParams.get("pid") !== null ? Number(searchParams.get("pid")) : null;
  const openPayment = useOpenTask({
    taskKey: "payment",
  });
  const openProgress = useOpenTask({
    taskKey: "progress",
  });
  const openChat = useOpenTask({
    taskKey: "chat",
  });
  const unifiedNavigate = useUnifiedNavigate();

  const isInappWebview = useIsInappWebview();

  dayjs.extend(duration);

  const [user, setUser] = useRecoilState(userAtom);
  const [patientState, setPatientState] = useRecoilState(patientAtom);

  const [userWebChatState, setUserWebChatState] =
    useRecoilState(userWebChatAtom);
  const { isIOS } = useDetectIOS();

  const isCoach =
    user?.accessToken !== undefined &&
    user?.role !== undefined &&
    user?.role !== "patient";

  const isPhone = isMobile && !isTablet;
  const resetPid = useResetRecoilState(patientAtom);

  // const decodedTaskKey = decodeURIComponent(taskKey || "");
  useEffect(() => {
    if (pid && isCoach) {
      setPatientState({
        patientId: pid,
      });
    }
  }, [pid]);

  useEffect(() => {
    if (isCoach) {
      resetPid();
    }
  }, []);

  const queryFn = useCallback(() => userApi.getDetail(), []);

  const { removeTab: removePayment } = useRemoveTab();
  const refetchTaskList = useRefetchTaskList();

  // 개념화노트, 생각지도 데이터 세팅
  const { isConceptNoteFetching, isConceptFetching } = useSetConceptData();

  useQuery([USER_DOMAIN, USER_DETAIL_ENDPOINT], queryFn, {
    enabled:
      !!axiosInstance.defaults.headers["X-Auth-Token"] && !!user?.accessToken, // webview에서 토큰 받아오기 전에 못불러오게

    onSuccess: (data) => {
      if (data && data.result) {
        if (data.result.role === "patient") {
          setPatientState({
            patientId: Number(data.result.userId),
            nickname: data.result.nickname,
            plan: data.result.plan,
          });
        }
        //결제 탭 닫기
        if (data.result.subscription?.isSubscribing) {
          if (taskKey === "payment" && !user?.subscription?.isSubscribing) {
            removePayment();
          }

          //자물쇠 풀기
          refetchTaskList();
        }

        if (user?.isOnboardingRequired && !data.result.isOnboardingRequired) {
          // 닫기 토스페이먼츠 창 닫기
          sendMessage(
            JSON.stringify({
              event: "navigate_back",
            })
          );

          if (!user?.isCommitmentRequired && data.result.isCommitmentRequired) {
            if (user?.refundPolicyType === "c") {
              unifiedNavigate("/commitment", {
                type: "card",
                animation: "none",
                gestureEnabled: false,
                replace: true,
              });
            }
          }
        }

        setUser((user) => ({
          ...user,
          ...data.result,
        }));
      }
    },
  });

  useEffect(() => {
    if (user && user.accessToken) {
      setAuthHeaders(user.accessToken);
      sendMessage(
        JSON.stringify({
          event: "value",
          params: { accessToken: user.accessToken },
        })
      );
      if (isPayment) {
        openPayment();
      }
    } else {
      // isInappWebview가 false(= 모바일 브라우저)일 때만 딥링크
      if (isInappWebview === false) {
        if (isPhone) {
          window.location.href = deepLinkData.install.url;
        } else {
          navigate("/login", { replace: true });
        }
      } else if (isInappWebview === true) {
        // 앱 내 웹뷰: 기존 로직 유지
        window.location.href = getRedirectPath(user);
      } else {
        // isInappWebview가 아직 결정되지 않은(undefined) 상태라면 대기
        // (아무 것도 하지 않음 → fallback 1)에서 곧 true/false가 들어옵니다)
      }
    }
  }, [
    user?.accessToken,
    isInappWebview,
    user,
    isPayment,
    openPayment,
    isPhone,
    navigate,
  ]);

  useEffect(() => {
    if (!isInappWebview) {
      navigate(`/`, { replace: true });

      if (patientState?.patientId) {
        if (isCoach && (patientState?.unreadMessageCount || 0) > 0) {
          openChat();
        } else {
          openProgress();
        }
      }
    }
  }, [patientState?.patientId]);

  const getDetailQueryFn = useCallback(() => chatApi.getDetail(), []);

  const { refetch, isLoading } = useQuery(
    [CHAT_DOMAIN, CHAT_DETAIL_ENDPOINT, user?.userId],
    getDetailQueryFn,
    {
      enabled: !!user?.userId && user.role === "patient" && isInappWebview,
      refetchIntervalInBackground: false,
      onSuccess: (data) => {
        if (data && data.result) {
          setUserWebChatState(data.result);
        }
      },
    }
  );

  useEffect(() => {
    if (user?.isOnboardingRequired) {
      unifiedNavigate("/onboarding_new/load/1", {
        type: "card",
        animation: "none",
        gestureEnabled: false,
      });
    } else if (user?.isCommitmentRequired) {
      if (user?.refundPolicyType === "c") {
        unifiedNavigate("/commitment", {
          type: "card",
          animation: "none",
          gestureEnabled: false,
          replace: true,
        });
      }
    }
  }, [
    user?.isOnboardingRequired,
    user?.isCommitmentRequired,
    user?.refundPolicyType,
  ]);

  return (user?.accessToken &&
    !!axiosInstance.defaults.headers["X-Auth-Token"]) ||
    (!isInappWebview && !isPhone) ? (
    <Sheet
      sx={{
        backgroundColor: "background.level2",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "row",
        flexGrow: 0,
        whiteSpace: "pre-line",
      }}
      className="w-full h-full"
    >
      {isCoach ? <SidebarLayoutForCoach /> : <SidebarLayoutPatient />}

      <Stack
        sx={{
          flex: 1,
          backgroundColor: "background.level1",
          display: "flex",
          flexDirection: "row",
          width: "100%",
          minWidth: 0,
        }}
      >
        {taskKey && <Task key={`task_${taskKey}`} taskKey={taskKey} />}
      </Stack>

      {isCoach && patientState?.patientId && (
        <Assistant patientId={patientState.patientId} />
      )}
    </Sheet>
  ) : (
    <EmptyPage />
  );
}
